import { useCallback, useMemo } from 'react';

import logger from '@/providers/Logging';

type LogSeverityLevel = 'warn' | 'info' | 'error';
type LogData = Record<string, unknown>;

interface QuestionnaireLoggerMethods {
  error: (message: string, metadata?: LogData) => void;
  warn: (message: string, metadata?: LogData) => void;
  info: (message: string, metadata?: LogData) => void;
}

/**
 * Hook that provides logging methods with automatic questionnaireId context
 */
const useQuestionnaireLogger = (questionnaireId: string): QuestionnaireLoggerMethods => {
  const logWithContext = useCallback(
    (level: LogSeverityLevel, message: string, metadata?: LogData) => {
      // Warn if questionnaireId is provided in metadata - it should come from the hook parameter
      if (metadata && 'questionnaireId' in metadata) {
        logger.warn('useQuestionnaireLogger: questionnaireId found in metadata. Using hook parameter instead.', {
          hookQuestionnaireId: questionnaireId,
          message,
          metadataQuestionnaireId: metadata.questionnaireId,
        });
      }

      // Remove questionnaireId from metadata if present and always use the hook parameter
      const { questionnaireId: _, ...cleanMetadata } = metadata || {};

      const contextualMetadata = {
        questionnaireId,
        ...cleanMetadata,
      };

      logger[level](message, contextualMetadata);
    },
    [questionnaireId],
  );

  const methods = useMemo(
    () => ({
      error: (message: string, metadata?: LogData) => logWithContext('error', message, metadata),
      info: (message: string, metadata?: LogData) => logWithContext('info', message, metadata),
      warn: (message: string, metadata?: LogData) => logWithContext('warn', message, metadata),
    }),
    [logWithContext],
  );

  return methods;
};

export default useQuestionnaireLogger;
